import { Alert, Box, Button, Grid, Tooltip, Typography } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getEInvoiceSender } from 'src/shared/services/electronicInvoiceSenderService';
import { FormProvider, useForm } from 'react-hook-form';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';

type EInvoiceConnectEditForm = {
  party_id: string;
  vatNo: string;
  emailNotification: string;
};

type EInvoiceConnectEditModalProps = {
  open: boolean;
  onClose: () => void;
  invoiceGroupId: number;
  emailNotification?: string;
  onSaveEmailNotification?: (email: string) => Promise<void>;
  onDisconnect?: () => Promise<void>;
};

export const EInvoiceConnectEditModal = (props: EInvoiceConnectEditModalProps) => {
  const { open, onClose, invoiceGroupId, emailNotification = '', onSaveEmailNotification, onDisconnect } = props;
  const { t } = useTranslation(['invoice', 'member', 'common']);
  const { currentUser } = useGlobals();
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [confirmDisconnectOpen, setConfirmDisconnectOpen] = useState<boolean>(false);

  const memberLabel = t('member:editMember.memberLabel').toLowerCase();
  const otherLabel = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.OtherLabel, t('member:otherContact.label')).toLowerCase();

  const emailFieldTooltip = [
    t('invoice:adminInvoiceGroupDetailPage.eInvoiceEmailNotificationTooltipPart1'),
    memberLabel,
    t('invoice:adminInvoiceGroupDetailPage.eInvoiceEmailNotificationTooltipConnector'),
    otherLabel,
    t('invoice:adminInvoiceGroupDetailPage.eInvoiceEmailNotificationTooltipPart2'),
  ].join(' ');

  const schema = yup.object().shape({
    party_id: yup.string(),
    vatNo: yup.string(),
    emailNotification: yup.string().email(t('common:validationMessages.email')).required(t('common:validationMessages.required')),
  });

  const formMethods = useForm<EInvoiceConnectEditForm>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      party_id: '',
      vatNo: '',
      emailNotification,
    },
  });

  const { reset, handleSubmit } = formMethods;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getEInvoiceSender(invoiceGroupId);
      if (isMounted() && res.data) {
        reset({
          party_id: res.data.party_id?.toString() || '',
          vatNo: res.data.vatNo || '',
          emailNotification,
        });
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [invoiceGroupId, isMounted, reset, emailNotification]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const onSave = handleSubmit(async (data) => {
    if (!onSaveEmailNotification) return;
    setSaving(true);
    try {
      await onSaveEmailNotification(data.emailNotification);
      onClose();
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setSaving(false);
    }
  });

  const emailFieldLabel = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant='inherit'>{t('invoice:adminInvoiceGroupDetailPage.electronicInvoiceEmailNotification')}</Typography>
      <Tooltip title={emailFieldTooltip} arrow placement='top'>
        <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
      </Tooltip>
    </Box>
  );

  return (
    <>
      <GenericDialog
        open={open}
        onClose={onClose}
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onSave={() => {}}
        maxWidth='sm'
        dialogTitle={t('invoice:adminInvoiceGroupDetailPage.eInvoiceConnectionTitle')}
        dialogContent={
          <FormProvider {...formMethods}>
            <Grid container spacing={2} p={0}>
              <Grid item xs={12}>
                <Alert
                  icon={false}
                  severity='info'
                  sx={{
                    '& .MuiAlert-message': { width: '100% !important' },
                    fontWeight: 500,
                    m: -2,
                  }}
                >
                  {t('invoice:adminInvoiceGroupDetailPage.eInvoiceConnectionInfo')}
                </Alert>
              </Grid>
              <LoadingOverlay loading={loading} />
              <Grid item xs={12} mt={2}>
                <ControlledTextField disabled label={t('invoice:adminInvoiceGroupDetailPage.eInvoicePartyId')} name='party_id' />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField disabled label={t('invoice:adminInvoiceGroupDetailPage.eInvoiceVat')} name='vatNo' />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField label={emailFieldLabel} name='emailNotification' />
              </Grid>
            </Grid>
          </FormProvider>
        }
        dialogActions={
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Box>
              <Button variant='outlined' color='error' disableElevation onClick={() => setConfirmDisconnectOpen(true)}>
                {t('common:button.removeConnection')}
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant='outlined' color='primary' disableElevation onClick={onClose}>
                {t('common:button.cancel')}
              </Button>
              <Button variant='contained' color='primary' disableElevation disabled={saving} onClick={onSave}>
                {t('common:button.save')}
              </Button>
            </Box>
          </Box>
        }
        showDialogActions
      />
      <DeleteConfirmationDialog
        open={confirmDisconnectOpen}
        title={t('invoice:adminInvoiceGroupDetailPage.eInvoiceDisconnectConfirmTitle')}
        value=''
        modaltext={t('invoice:adminInvoiceGroupDetailPage.eInvoiceDisconnectConfirmText')}
        onClose={async (value) => {
          setConfirmDisconnectOpen(false);
          if (value && onDisconnect) {
            await onDisconnect();
            onClose();
          }
        }}
      />
    </>
  );
};
